/**
 * File URL Service - Provides utilities for generating and resolving file URLs
 *
 * Enhanced File Viewer API:
 * - getFileForViewer(fileId, includeRelatedFiles): Gets enhanced file data with tokenized URLs
 *   - fileId: The ID of the file
 *   - includeRelatedFiles: Whether to include related exam files for context
 *   - Returns: Promise<FileViewerModel|null> - Enhanced file data or null if not found
 *
 * Enhanced File Listing API:
 * - getFilesByExamId(examId, includeViewerProcessing): Gets file list with optional URL processing
 *   - examId: The exam ID to get files for
 *   - includeViewerProcessing: Whether to include processed viewer URLs (default: false)
 *   - Returns: Promise<FileModel[]> - File list, optionally with processed URLs
 *
 * - getFilesByPatientId(patientId, includeViewerProcessing): Gets file list with optional URL processing
 *   - patientId: The patient ID to get files for
 *   - includeViewerProcessing: Whether to include processed viewer URLs (default: false)
 *   - Returns: Promise<FileModel[]> - File list, optionally with processed URLs
 *
 * Example usage:
 *   // Lightweight file listing for display
 *   const files = await FileUrlService.getFilesByExamId(123);
 *
 *   // Enhanced file listing with processed URLs for immediate viewing
 *   const filesWithUrls = await FileUrlService.getFilesByExamId(123, true);
 *
 *   // Get specific file details for viewer
 *   const fileData = await FileUrlService.getFileForViewer(456, true);
 *   if (fileData) {
 *     console.log('Primary URL:', fileData.primaryViewUrl);
 *     console.log('Has backup URLs:', fileData.hasBackupUrls);
 *     console.log('Related files:', fileData.relatedExamFiles);
 *   }
 *
 * This provides optimal performance by allowing lightweight listing when only file info is needed,
 * and enhanced processing when URLs are actually required for viewing.
 */
import { FileModel, FileViewerModel } from 'models';

import { apiClient } from 'core/api/globals';

import { FileRouteService } from 'features/file/services/file-route-service';

// Legacy function: we are appending date_modified to the file url so that we can rerender iframe
// when the file is modified (rotated) but the file url stays the same
const getFileUrl = (fileId: number, fileUrl: string, dateModified?: string | null, accessToken?: string | null): string => {
  const params = new URLSearchParams({
    url: fileUrl,
    access_token: accessToken || '',
    ...(dateModified ? { date_modified: dateModified } : {}),
  });
  return `${FileRouteService.getByIdRoute(fileId)}?${params.toString()}`;
};

// Legacy function: resolves file URL using the old approach
const resolveFileUrl = (file: FileModel, accessToken?: string | null): string => {
  if (!file.url) {
    return '';
  }

  if (file.fileType === 'LINK') {
    return file.url;
  }

  return getFileUrl(file.id, file.url, file.modified, accessToken);
};

/**
 * Gets files by exam ID with optional viewer URL processing.
 * Set includeViewerProcessing to true when you need URLs ready for immediate viewing.
 * Leave false (default) for lightweight file listing.
 */
const getFilesByExamId = async (examId: number, includeViewerProcessing = false): Promise<FileModel[]> => {
  try {
    return await apiClient.filesClient.getFilesByExamId(examId, includeViewerProcessing);
  } catch (error) {
    console.error('Error getting files by exam ID:', error instanceof Error ? error.message : String(error));
    return [];
  }
};

/**
 * Gets files by patient ID with optional viewer URL processing.
 * Set includeViewerProcessing to true when you need URLs ready for immediate viewing.
 * Leave false (default) for lightweight file listing.
 */
const getFilesByPatientId = async (patientId: number, includeViewerProcessing = false): Promise<FileModel[]> => {
  try {
    return await apiClient.filesClient.getFilesByPatientId(patientId, includeViewerProcessing);
  } catch (error) {
    console.error('Error getting files by patient ID:', error instanceof Error ? error.message : String(error));
    return [];
  }
};

/**
 * Gets enhanced file data for viewing including tokenized URLs and related data.
 * This is the recommended approach for file viewing when you need detailed viewer data.
 */
const getFileForViewer = async (fileId: number, includeRelatedFiles = false): Promise<FileViewerModel | null> => {
  try {
    const url = FileRouteService.getFileViewerRoute(fileId, includeRelatedFiles);
    const options = await apiClient.httpClient.createStandardOptions('GET', null);

    const response = await fetch(url, options);

    if (!response.ok) {
      if (response.status === 404) {
        return null; // File not found
      }
      throw new Error(`Failed to get file data: ${response.status}`);
    }

    const data = await response.json();
    return data.result;
  } catch (error) {
    console.error('Error getting file for viewer:', error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Gets the appropriate view URL from FileViewerModel based on view type
 */
const getViewUrlFromFileData = (fileData: FileViewerModel | null, viewType = 'primary'): string | null => {
  if (!fileData) return null;

  switch (viewType.toLowerCase()) {
    case 'secondary':
      return fileData.secondaryViewUrl ?? fileData.primaryViewUrl ?? null;
    case 'tertiary':
      return fileData.tertiaryViewUrl ?? fileData.secondaryViewUrl ?? fileData.primaryViewUrl ?? null;
    case 'primary':
    default:
      return fileData.primaryViewUrl ?? null;
  }
};

export const FileUrlService = {
  // Legacy functions (kept for backward compatibility)
  getFileUrl,
  resolveFileUrl,

  // Enhanced file listing functions
  getFilesByExamId,
  getFilesByPatientId,

  // Enhanced file viewer functions
  getFileForViewer,
  getViewUrlFromFileData,
};
